/**
 * A text field that tags names as you type (docs/mentions.md, flag `mentionsUI`; owner, 2026-10-19:
 * "It'll just like open the menu to select a peon when you type at, or if you type the name, it will
 * by default tag them"). The field is the caller's own textarea or input, with its classes, frame
 * and font; with the flag on two things join it:
 * - The tags, drawn as you type: a mirror of the words lies exactly over the field, its plain words
 *   invisible and each tagged name a pill in the peon's colour (MentionText's), so the field's own
 *   words show through everywhere else. It copies the field's font, padding and border widths, and
 *   follows its scroll. Decoration only: the field is still the one thing typed in and read out.
 * - The picker: typing "@" opens a row of the household's names right above the field (the notes'
 *   "Who" chips, a small square of each peon's colour before the name), filtered by what follows the
 *   "@". A tap, or Enter or Tab on the highlighted one (arrow keys move it), puts the plain name and
 *   a space in place of the "@" and the letters typed after it. Escape, a tap elsewhere, or a space
 *   that matches nobody closes it and leaves the words as typed.
 * With the flag off, the field alone, exactly as before.
 */
import "./mentions.css";
import { useLayoutEffect, useRef, useState } from "preact/hooks";
import type { JSX, Ref, RefObject } from "preact";
import { useMentionRoster } from "../mentions";
import { mentionNodes } from "./MentionText";
import { applyMention, mentionMatches, mentionPickerOpen, mentionQuery } from "../../shared/mentions.ts";

type FieldEl = HTMLTextAreaElement | HTMLInputElement;

/** The field's own look the mirror takes on, so its words land exactly on the field's. */
const COPIED = [
  "fontFamily",
  "fontSize",
  "fontWeight",
  "fontStyle",
  "lineHeight",
  "letterSpacing",
  "wordSpacing",
  "textIndent",
  "textTransform",
  "paddingTop",
  "paddingRight",
  "paddingBottom",
  "paddingLeft",
  "borderTopWidth",
  "borderRightWidth",
  "borderBottomWidth",
  "borderLeftWidth",
  "boxSizing",
  "textAlign",
] as const;

type Common = {
  value: string;
  /** The words after a keystroke or a pick. */
  onValue: (value: string) => void;
  /** The field, for the caller's focus. */
  fieldRef?: RefObject<FieldEl>;
  /** The picker's id prefix (unique on the page). */
  pickerId: string;
};

export type MentionFieldProps =
  | (Common & { as: "textarea" } & Omit<JSX.IntrinsicElements["textarea"], "value" | "onInput" | "ref">)
  | (Common & { as: "input" } & Omit<JSX.IntrinsicElements["input"], "value" | "onInput" | "ref">);

export function MentionField(props: MentionFieldProps) {
  const { as, value, onValue, fieldRef, pickerId, ...rest } = props;
  const roster = useMentionRoster();
  const ownRef = useRef<FieldEl>(null);
  const ref = (fieldRef ?? ownRef) as RefObject<FieldEl>;
  const mirrorRef = useRef<HTMLDivElement>(null);
  const [caret, setCaret] = useState<number | null>(null);
  const [active, setActive] = useState(0);
  // The "@" the picker was closed on (Escape, a tap away): it stays closed until that "@" is gone.
  const [dismissed, setDismissed] = useState<number | null>(null);

  const query = roster && caret !== null ? mentionQuery(value, caret) : null;
  const open = !!roster && !!query && query.start !== dismissed && mentionPickerOpen(roster.names, query);
  const matches = open && roster && query ? mentionMatches(roster.names, query.query) : [];
  const shown = open && matches.length > 0;
  const activeIdx = Math.min(active, Math.max(0, matches.length - 1));

  // The mirror takes the field's look once laid out, and again when the field is resized.
  useLayoutEffect(() => {
    const field = ref.current;
    const mirror = mirrorRef.current;
    if (!roster || !field || !mirror) return;
    const copy = () => {
      const cs = getComputedStyle(field);
      for (const k of COPIED) (mirror.style as unknown as Record<string, string>)[k] = cs[k];
      // A scrollbar narrows the field's words; the mirror's padding takes its width.
      const bars = field.offsetWidth - field.clientWidth - parseFloat(cs.borderLeftWidth) - parseFloat(cs.borderRightWidth);
      if (bars > 0) mirror.style.paddingRight = `${parseFloat(cs.paddingRight) + bars}px`;
      mirror.scrollTop = field.scrollTop;
      mirror.scrollLeft = field.scrollLeft;
    };
    copy();
    if (typeof ResizeObserver !== "function") return;
    const ro = new ResizeObserver(copy);
    ro.observe(field);
    return () => ro.disconnect();
  }, [!!roster, value]);

  if (!roster) {
    // The flag off (or the names not read yet): the field alone, as it always was.
    return as === "textarea" ? (
      <textarea {...(rest as JSX.IntrinsicElements["textarea"])} ref={ref as Ref<HTMLTextAreaElement>} value={value} onInput={(e) => onValue(e.currentTarget.value)} />
    ) : (
      <input {...(rest as JSX.IntrinsicElements["input"])} ref={ref as Ref<HTMLInputElement>} value={value} onInput={(e) => onValue(e.currentTarget.value)} />
    );
  }

  const track = (el: FieldEl) => {
    setCaret(el.selectionStart);
    const q = mentionQuery(el.value, el.selectionStart ?? 0);
    if (!q) setDismissed(null);
    if (mirrorRef.current) {
      mirrorRef.current.scrollTop = el.scrollTop;
      mirrorRef.current.scrollLeft = el.scrollLeft;
    }
  };

  const pick = (name: string) => {
    const field = ref.current;
    if (!field || !query || caret === null) return;
    const next = applyMention(value, query, caret, name);
    onValue(next.text);
    setCaret(next.caret);
    setActive(0);
    requestAnimationFrame(() => {
      field.focus();
      field.setSelectionRange(next.caret, next.caret);
    });
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (!shown) return;
    if (e.key === "ArrowDown" || e.key === "ArrowRight") {
      e.preventDefault();
      setActive((activeIdx + 1) % matches.length);
    } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
      e.preventDefault();
      setActive((activeIdx - 1 + matches.length) % matches.length);
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      pick(matches[activeIdx]);
    } else if (e.key === "Escape") {
      // Closes the picker only; the sheet's own Escape waits for the next press.
      e.preventDefault();
      setDismissed(query!.start);
    }
  };

  const listId = `${pickerId}-names`;
  const fieldProps = {
    ...rest,
    ref,
    value,
    class: `${(rest as { class?: string }).class ?? ""} mention-input`,
    onInput: (e: JSX.TargetedEvent<FieldEl>) => {
      onValue(e.currentTarget.value);
      setActive(0);
      track(e.currentTarget);
    },
    onKeyDown,
    onKeyUp: (e: JSX.TargetedKeyboardEvent<FieldEl>) => {
      if (e.key.startsWith("Arrow") && shown) return;
      track(e.currentTarget);
    },
    onClick: (e: JSX.TargetedMouseEvent<FieldEl>) => track(e.currentTarget),
    onSelect: (e: JSX.TargetedEvent<FieldEl>) => track(e.currentTarget),
    onScroll: (e: JSX.TargetedEvent<FieldEl>) => track(e.currentTarget),
    onBlur: () => {
      if (query) setDismissed(query.start);
    },
    role: shown ? "combobox" : undefined,
    "aria-expanded": shown ? true : undefined,
    "aria-controls": shown ? listId : undefined,
    "aria-autocomplete": "list",
    "aria-activedescendant": shown ? `${listId}-${activeIdx}` : undefined,
  };

  return (
    <div class="mention-field">
      {shown && (
        <div class="segmented note-chips mention-picker" role="listbox" id={listId} aria-label="Tag a peon">
          {matches.map((name, i) => {
            const c = roster.colourOf(name);
            return (
              <button
                key={name}
                type="button"
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === activeIdx}
                class={i === activeIdx ? "active" : ""}
                // Keep the field's focus (and its caret) through the tap.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(name)}
              >
                <span class="mention-pip" aria-hidden="true" style={{ background: c.swatch }} />
                {name}
              </button>
            );
          })}
        </div>
      )}
      <div class={`mention-box${as === "input" ? " single" : ""}`}>
        {as === "textarea" ? (
          <textarea {...(fieldProps as unknown as JSX.IntrinsicElements["textarea"])} />
        ) : (
          <input {...(fieldProps as unknown as JSX.IntrinsicElements["input"])} />
        )}
        <div class="mention-mirror" ref={mirrorRef} aria-hidden="true">
          <span class="mention-line">
            {mentionNodes(value, roster)}
            {"​"}
          </span>
        </div>
      </div>
    </div>
  );
}
